import { useState } from 'react'
import classNames from 'classnames'
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from '@radix-ui/react-dialog'

import { Body, Button, Divider } from '@foxino/components-web'
import { ChevronRight, LogOut02, Settings02, UserCircle, XClose } from '@foxino/icons-react'

import { logout } from '@app/app/auth/actions/authActions'
import type { GetUserProfileQuery } from '@app/data'
import { appLanguageToCode, useTranslation } from '@app/locales'
import i18n, { languageCodeToSupportedLanguage } from '@app/locales/i18n'
import { useUserProfile } from '@app/modules/settings/data/useUserProfile'
import { SettingsSection } from '@app/modules/settings/model/enums'
import type { UserFormValues } from '@app/modules/settings/model/types'
import cancelEvent from '@app/utils/cancelEvent'

import packageJson from '../../../../../../package.json'
import ReportBugIcon from '../../../../../assets/images/reportBug.svg?react'
import { BugReportForm } from './BugReportForm'
import { GeneralForm } from './GeneralForm'
import { ProfileForm } from './ProfileForm'

const settingSectionToProps: Record<SettingsSection, { title: string; Icon: typeof UserCircle }> = {
  [SettingsSection.PROFILE]: {
    title: 'profile.menu',
    Icon: UserCircle
  },
  [SettingsSection.PREFERENCES]: {
    title: 'preferences.menu',
    Icon: Settings02
  },
  [SettingsSection.REPORT_BUG]: {
    title: 'reportBug.menu',
    Icon: ReportBugIcon as typeof UserCircle
  }
}

type Props = {
  userProfile: GetUserProfileQuery['userProfile']
  dialogSection: SettingsSection | undefined
  isPending: boolean
  userActions: ReturnType<typeof useUserProfile>['actions']
  onClose: () => void
}

export const MainSettingsDialog = ({
  onClose,
  userProfile,
  dialogSection = SettingsSection.PROFILE,
  userActions,
  isPending
}: Props) => {
  const { t } = useTranslation(['common', 'settings'])

  const [section, setSection] = useState<SettingsSection>(dialogSection)

  const handleOnLanguageChange = async (value: string) => {
    const lang = appLanguageToCode[value]
    await i18n.changeLanguage(lang)
    localStorage.setItem('lng', value)

    window.location.reload()
  }

  const handleOnUpdateProfile = async (data: UserFormValues) => {
    const { appLanguage } = data

    await userActions.updateProfile(data)

    if (appLanguage && appLanguage !== languageCodeToSupportedLanguage[i18n.language]) {
      handleOnLanguageChange(appLanguage)
    }
  }

  const renderSection = () => {
    switch (section) {
      case SettingsSection.PROFILE:
        return <ProfileForm userProfile={userProfile} onSubmit={handleOnUpdateProfile} isPending={isPending} />
      case SettingsSection.PREFERENCES:
        return <GeneralForm userProfile={userProfile} onSubmit={handleOnUpdateProfile} />
      case SettingsSection.REPORT_BUG:
        return <BugReportForm userProfile={userProfile} />
      default:
        return null
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay
          className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-md"
          onClick={(event) => {
            cancelEvent(event)
            onClose()
          }}
        />

        <DialogContent
          className={classNames(
            'fixed left-1/2 top-1/2 z-[101] flex h-[510px] max-h-screen w-[887px] max-w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-primary-900'
          )}
          aria-describedby={undefined}
        >
          <DialogTitle className="hidden">{t('title')}</DialogTitle>

          <div className="flex w-full">
            {/* Left Part */}
            <div className="flex min-w-[226px] flex-col overflow-y-auto rounded-l-2xl bg-primary-800 p-[10px]">
              <div className="mb-6 flex w-full items-center gap-[10px]">
                <Button size="xs" color="secondary" LeadingIcon={XClose} onClick={onClose} className="text-white" />
                <Body size="2xl" weight="bold" className="text-white">
                  {t('title')}
                </Body>
              </div>
              <div className="flex flex-1 flex-col justify-between">
                <div className="mb-12 flex flex-col overflow-y-auto">
                  {/* Menu items */}
                  {Object.keys(SettingsSection).map((key) => {
                    const sectionKey = SettingsSection[key as keyof typeof SettingsSection]
                    const { Icon, title } = settingSectionToProps[sectionKey]
                    const isActive = sectionKey === section

                    return (
                      <div className="flex flex-col" key={key}>
                        <div
                          className={classNames(
                            'flex cursor-pointer items-center justify-between gap-2.5 rounded-lg border py-2 hover:bg-primary-700',
                            isActive ? 'border-primary-500 px-2.5' : 'border-transparent px-1'
                          )}
                          onClick={() => setSection(sectionKey)}
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className={classNames('size-[18px]', isActive ? 'text-white' : 'text-white/50')} />
                            <Body size="md" weight={isActive ? 'semibold' : 'regular'} className="text-white">
                              {t(title)}
                            </Body>
                          </div>
                          <ChevronRight className="size-4 text-white" />
                        </div>

                        <Divider className="my-2 bg-primary-700" />
                      </div>
                    )
                  })}
                </div>

                {/* Version and logout */}
                <div className="">
                  <p className="w-full pb-2.5 text-center text-xs text-white/20">Foxino {packageJson.version}</p>

                  <button
                    className="flex w-full items-center gap-2 rounded-lg bg-primary-900 px-3 py-2 text-white hover:bg-primary-1000"
                    onClick={logout}
                  >
                    <LogOut02 className="w-4 text-error-600" />
                    {t('logout')}
                  </button>
                </div>
              </div>
            </div>

            {/* RightPart */}
            <div className="size-full overflow-auto bg-primary-1050">{renderSection()}</div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
